"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LiquidationCalculator = void 0;
const trade_utils_lib_1 = require("trade_utils_lib");
/**
 * 爆仓价基本原理
 * y = 其他因素所消耗的保证金
 * 参考公式：assets = y + 仓位数量x(P-持仓均价)x仓位方向(买入做多/卖出做空) + 仓位数量xPx当前仓位价值对应档位的维持保证金率
 * 参考公式：Available = 手续费 + 盈亏 + 维持保证金
 */
var LiquidationCalculator;
(function (LiquidationCalculator) {
    // 参入每档的最小值
    LiquidationCalculator.processLeverageConfigs = (leverageConfigs) => {
        const newConfigs = leverageConfigs.slice();
        return (newConfigs
            // 升序
            .sort((a, b) => a.row_num - b.row_num)
            .map((config, index) => {
            let notional_min = "0";
            if (index > 0) {
                const lastRow = newConfigs[index - 1];
                // 升序， 上一档的最大值为新一档的最小值
                notional_min = lastRow.notional_limit;
            }
            return Object.assign(Object.assign({}, config), { notional_min: notional_min });
        }));
    };
    /**
     * 计算仓位所需维持保证金
     * @param leverageConfigs 维持保证金配置信息
     * @param value 仓位价值
     */
    LiquidationCalculator.calcuMaintenanceMargin = (leverageConfigs, value) => {
        if (!value || new trade_utils_lib_1.SafeDecimal(value).eq(0) || !leverageConfigs || !leverageConfigs.length) {
            return new trade_utils_lib_1.SafeDecimal(0);
        }
        // 计算每一层的最小名义价值
        // 判断逻辑某一层为 > min && <= max
        const parseConfig = LiquidationCalculator.processLeverageConfigs(leverageConfigs);
        // 当前仓位的最大名义价值
        const curValue = new trade_utils_lib_1.SafeDecimal(value);
        const curRow = parseConfig.find((row) => {
            return curValue.greaterThan(new trade_utils_lib_1.SafeDecimal(row.notional_min)) && curValue.lessThan(new trade_utils_lib_1.SafeDecimal(row.notional_limit));
        }) || parseConfig[parseConfig.length - 1];
        // 计算维持保证金
        // 当前档速算额 + （仓位价值 - 上一档最大名义价值）* 当前档位维持保证金率
        return new trade_utils_lib_1.SafeDecimal(curRow.quick_deduction).add(curValue.sub(new trade_utils_lib_1.SafeDecimal(curRow.notional_min)).mul(new trade_utils_lib_1.SafeDecimal(curRow.maint_margin_ratio)));
    };
    // 计算爆仓价
    /**
     * 爆仓价基本原理
     * y = 其他因素所消耗的保证金
     * 参考公式: asset(资产) = y + 仓位数量x(P-持仓均价)x仓位方向(买入做多/卖出做空) + 仓位数量xPx当前仓位价值对应档位的维持保证金率
     * 参考公式: asset(资产) = 手续费 + 盈亏 + 维持保证金
     */
    LiquidationCalculator.calcuLiquidationPrice = (params) => {
        const { leverageConfigs, position, avgPrice, side, assets, otherSpending } = params;
        if (!leverageConfigs ||
            !leverageConfigs.length ||
            !position ||
            new trade_utils_lib_1.SafeDecimal(position).eq(0) ||
            !assets ||
            new trade_utils_lib_1.SafeDecimal(assets).eq(0)) {
            return new trade_utils_lib_1.SafeDecimal(0);
        }
        const assetsD = new trade_utils_lib_1.SafeDecimal(assets);
        const offsetD = new trade_utils_lib_1.SafeDecimal(otherSpending);
        const positionD = new trade_utils_lib_1.SafeDecimal(position).abs();
        const avgPriceD = new trade_utils_lib_1.SafeDecimal(avgPrice);
        const directionD = side === "short" ? new trade_utils_lib_1.SafeDecimal(1) : new trade_utils_lib_1.SafeDecimal(-1);
        // 倒序 从最高档开始爆仓
        const sortedLeverage = LiquidationCalculator.processLeverageConfigs(leverageConfigs).sort((a, b) => b.row_num - a.row_num);
        // 仓位数量为绝对值
        // assets = y + 仓位数量x(P-持仓均价)x仓位方向(买入做多 -1 or 卖出做空 1) + 仓位数量xPx当前仓位价值对应档位的维持保证金率
        // direction = (long -1 or short 1) // 控制计算浮动盈亏的方向
        // assets = offset + position * direction * (X - avgPrice) + mRate * (position * X) - quick_deduction
        // assets - offset + quick_deduction = position * direction * (X - avgPrice) + mRate * (position * X)
        // assets - offset + quick_deduction = position * direction * X - position * direction *  avgPrice + mRate * position * X
        // (assets - offset + quick_deduction) / position = direction * X - direction * avgPrice + mRate * X
        // (assets - offset + quick_deduction) / position + direction * avgPrice = direction * X + mRate * X
        // ((assets - offset + quick_deduction) / position + direction * avgPrice) / (direction + mRate) = x
        for (let i = 0; i < sortedLeverage.length; i++) {
            const { quick_deduction, notional_min, notional_limit, maint_margin_ratio, row_num } = sortedLeverage[i];
            // 速算额
            const quickDeductionD = new trade_utils_lib_1.SafeDecimal(quick_deduction);
            // 维持保证金率
            const mRateD = new trade_utils_lib_1.SafeDecimal(maint_margin_ratio);
            // 档位最小名义价值
            const minNotionalD = new trade_utils_lib_1.SafeDecimal(notional_min);
            // 档位最大名义价值
            const maxNotionalD = new trade_utils_lib_1.SafeDecimal(notional_limit);
            // 计算强平价
            const liquidationPrice = assetsD
                .sub(offsetD)
                .add(quickDeductionD)
                .div(positionD)
                .add(directionD.mul(avgPriceD))
                .div(directionD.add(mRateD));
            // 强平时的仓位价值
            const positionValue = positionD.mul(liquidationPrice);
            const isConfigValid = (positionValue.greaterThan(minNotionalD) && positionValue.lessThanOrEqualTo(maxNotionalD)) ||
                (i === 0 && positionValue.greaterThan(maxNotionalD));
            if (isConfigValid) {
                return liquidationPrice;
            }
        }
        return new trade_utils_lib_1.SafeDecimal(0);
    };
})(LiquidationCalculator = exports.LiquidationCalculator || (exports.LiquidationCalculator = {}));
